import { Suspense, useEffect, useState } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { motion, AnimatePresence } from 'framer-motion'
import { Earth3D } from './components/Earth3D'
import { ActionPanel } from './components/ActionPanel'
import { ParameterPanel } from './components/ParameterPanel'
import { LearningModal } from './components/LearningModal'
import { QuizModal } from './components/QuizModal'
import { useGameStore } from './store/gameStore'
import { Loader2, Play, History, Trophy, BookOpen } from 'lucide-react'
import { uiLogger } from './utils/logger'

function App() {
  const { 
    gameState, 
    createWorld, 
    isLoading, 
    error,
    resetError,
    selectedAction 
  } = useGameStore()
  
  const [showHistory, setShowHistory] = useState(false)
  const [showTutorial, setShowTutorial] = useState(true)
  const [showLearning, setShowLearning] = useState(false)
  const [showQuiz, setShowQuiz] = useState(false)
  const [currentLearningPoints, setCurrentLearningPoints] = useState<string[]>([])
  const [currentActionName, setCurrentActionName] = useState('')
  const [actionEffect, setActionEffect] = useState<string | null>(null)
  
  useEffect(() => {
    // アプリケーション起動ログ
    uiLogger.appStarted()
    
    // 初回アクセス時に世界を作成
    if (!gameState) {
      createWorld('My Earth')
    }
    
    // ゲーム状態の同期（クールダウンを正確に取得）
    const interval = setInterval(() => {
      // 定期的にゲーム状態を取得して同期
      const currentState = useGameStore.getState()
      if (currentState.worldId) {
        currentState.fetchGameState(currentState.worldId)
      }
    }, 1000)
    
    // TODO項目をログに記録
    uiLogger.todo('実績システムの実装', 'high')
    uiLogger.todo('サウンドエフェクトの追加', 'medium')
    uiLogger.todo('セーブ/ロード機能の実装', 'high')
    
    return () => clearInterval(interval)
  }, [])
  
  if (!gameState && isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-cyan-400" />
          <p className="text-xl">世界を創造中...</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="h-screen overflow-hidden relative">
      {/* 3D地球ビュー */}
      <div className="absolute inset-0">
        <Canvas camera={{ position: [0, 0, 8], fov: 45 }}>
          <Suspense fallback={null}>
            <Earth3D gameState={gameState} />
            <OrbitControls 
              enablePan={false}
              minDistance={5}
              maxDistance={15}
              autoRotate
              autoRotateSpeed={0.5}
            />
          </Suspense>
        </Canvas>
      </div>
      
      {/* UI オーバーレイ */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="h-full flex">
          {/* 左サイドバー - パラメータ */}
          <div className="w-80 p-4 pointer-events-auto">
            <ParameterPanel gameState={gameState} />
          </div>
          
          {/* 中央 - タイトルと情報 */}
          <div className="flex-1 p-4">
            <div className="text-center">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-600 bg-clip-text text-transparent">
                Earth Simulator
              </h1>
              <p className="text-sm text-gray-400 mt-2">
                世界と自分を育てる学習ゲーム
              </p>
              
              {gameState && (
                <div className="mt-4 inline-flex items-center space-x-4 text-sm">
                  <span className="glass-panel px-3 py-1">
                    {gameState.worldName}
                  </span>
                  <span className="glass-panel px-3 py-1">
                    {gameState.worldAge.toLocaleString()}年
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* 右サイドバー - アクション */}
          <div className="w-80 p-4 pointer-events-auto">
            <ActionPanel 
              onActionComplete={() => {
                const learningData = useGameStore.getState().getLearningData()
                if (learningData) {
                  setCurrentLearningPoints(learningData.points)
                  setCurrentActionName(learningData.actionName)
                  setShowLearning(true)
                }
              }}
              onQuizOpen={() => setShowQuiz(true)}
            />
            
            {/* ボタン群 */}
            <div className="mt-4 flex space-x-2">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex-1 glass-panel p-2 flex items-center justify-center space-x-1 hover:bg-white/20 transition"
              >
                <History className="w-4 h-4" />
                <span className="text-sm">履歴</span>
              </button>
              
              <button
                onClick={() => setShowTutorial(true)}
                className="flex-1 glass-panel p-2 flex items-center justify-center space-x-1 hover:bg-white/20 transition"
              >
                <BookOpen className="w-4 h-4" />
                <span className="text-sm">学習</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* 履歴パネル */}
      <AnimatePresence>
        {showHistory && gameState && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            className="absolute bottom-0 left-0 right-0 h-64 glass-panel m-4 p-4 overflow-y-auto pointer-events-auto"
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold text-cyan-300">世界の歴史</h3>
              <button
                onClick={() => setShowHistory(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-2">
              {gameState.history.slice().reverse().map((event, index) => (
                <div key={index} className="flex space-x-3 text-sm">
                  <span className="text-gray-500 font-mono">
                    {event.age.toLocaleString()}年
                  </span>
                  <span className="text-cyan-400">{event.event}</span>
                  <span className="text-gray-300">{event.description}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* チュートリアル */}
      <AnimatePresence>
        {showTutorial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 flex items-center justify-center pointer-events-auto"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="glass-panel p-8 max-w-2xl mx-4"
            >
              <h2 className="text-2xl font-bold mb-4 text-cyan-300">
                🌍 地球シミュレーターへようこそ！
              </h2>
              
              <div className="space-y-4 text-gray-300">
                <p>
                  あなたは地球の創造主となり、様々なアクションを通じて世界を育てていきます。
                </p>
                
                <div>
                  <h3 className="font-bold text-white mb-2">遊び方：</h3>
                  <ul className="list-disc list-inside space-y-1">
                    <li>右側のアクションパネルから行動を選択</li>
                    <li>エネルギーを消費してアクションを実行</li>
                    <li>世界のパラメータを観察して次の行動を決める</li>
                    <li>極端な環境にならないようバランスを保つ</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-bold text-white mb-2">学習ポイント：</h3>
                  <ul className="list-disc list-inside space-y-1">
                    <li>地球システムの相互作用を理解</li>
                    <li>気候変動のメカニズムを体験</li>
                    <li>生態系のバランスの重要性を学ぶ</li>
                    <li>持続可能な発展について考える</li>
                  </ul>
                </div>
              </div>
              
              <button
                onClick={() => setShowTutorial(false)}
                className="mt-6 action-button w-full"
              >
                ゲームを始める
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* 学習モーダル */}
      <LearningModal
        isOpen={showLearning}
        onClose={() => setShowLearning(false)}
        learningPoints={currentLearningPoints}
        actionName={currentActionName}
      />
      
      {/* クイズモーダル */}
      <QuizModal
        isOpen={showQuiz}
        onClose={() => setShowQuiz(false)}
        onAnswer={async (isCorrect, category) => {
          const currentState = useGameStore.getState()
          if (currentState.worldId) {
            await currentState.submitQuizAnswer(currentState.worldId, isCorrect, category)
          }
        }}
      />
      
      {/* エラー表示 */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-4 left-1/2 transform -translate-x-1/2 glass-panel px-4 py-2 text-red-400"
          >
            {error}
            <button
              onClick={resetError}
              className="ml-4 text-white"
            >
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default App